import { api } from "@/lib/api";

export const MAX_VIDEOS = 3;
export const MAX_MB = 100;
export const MAX_SECONDS = 60;
const EXT = /\.(mp4|m4v|mov|webm)$/i;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const validType = (file) => EXT.test(file.name || "") || /^video\/(mp4|quicktime|webm)/.test(file.type);

export const fixName = (file) => {
  if (EXT.test(file.name || "")) return file.name;
  const ext = file.type.includes("quicktime") ? "mov" : file.type.includes("webm") ? "webm" : "mp4";
  return `video.${ext}`;
};

export const getDuration = (file) => new Promise((resolve) => {
  const v = document.createElement("video");
  const url = URL.createObjectURL(file);
  const done = (d) => { URL.revokeObjectURL(url); resolve(d); };
  const t = setTimeout(() => done(null), 6000);
  v.preload = "metadata"; v.muted = true; v.playsInline = true;
  v.onloadedmetadata = () => { clearTimeout(t); done(Number.isFinite(v.duration) ? v.duration : null); };
  v.onerror = () => { clearTimeout(t); done(null); };
  v.src = url;
});

const recMime = () => {
  if (typeof MediaRecorder === "undefined") return null;
  return ["video/mp4;codecs=avc1", "video/mp4", "video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"].find((m) => MediaRecorder.isTypeSupported(m)) || null;
};

// Re-encodes to 720p in real time (as long as the clip lasts).
export const compress720 = (file, onProgress) => new Promise((resolve, reject) => {
  const mime = recMime();
  const canvas = document.createElement("canvas");
  if (!mime || !canvas.captureStream) return reject(new Error("unsupported"));
  const v = document.createElement("video");
  const url = URL.createObjectURL(file);
  v.muted = true; v.playsInline = true; v.src = url;
  v.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode")); };
  v.onloadedmetadata = () => {
    const s = Math.min(1, 720 / Math.min(v.videoWidth, v.videoHeight));
    canvas.width = Math.round((v.videoWidth * s) / 2) * 2; canvas.height = Math.round((v.videoHeight * s) / 2) * 2;
    const ctx = canvas.getContext("2d");
    const rec = new MediaRecorder(canvas.captureStream(30), { mimeType: mime, videoBitsPerSecond: 2_500_000 });
    const parts = [];
    rec.ondataavailable = (e) => e.data.size && parts.push(e.data);
    rec.onstop = () => {
      URL.revokeObjectURL(url);
      const type = mime.split(";")[0];
      resolve(new File(parts, `video-720p.${type === "video/mp4" ? "mp4" : "webm"}`, { type }));
    };
    let raf;
    const draw = () => { ctx.drawImage(v, 0, 0, canvas.width, canvas.height); onProgress?.(Math.min(99, Math.round((v.currentTime / v.duration) * 100))); raf = requestAnimationFrame(draw); };
    v.onended = () => { cancelAnimationFrame(raf); rec.stop(); };
    rec.start(1000);
    v.play().then(draw).catch((e) => { URL.revokeObjectURL(url); reject(e); });
  };
});

const withRetry = async (fn, tries = 4) => {
  for (let i = 0; ; i++) {
    try { return await fn(); } catch (e) {
      const s = e?.response?.status;
      if (i >= tries - 1 || (s && s >= 400 && s < 500 && s !== 408 && s !== 429)) throw e;
      await sleep(1000 * 2 ** i);
    }
  }
};

// Uploads (or resumes) in chunks, then waits for server processing. `st` keeps {id, next, parts, chunk} across retries.
export const uploadVideo = async (file, st, onProgress) => {
  const stop = () => { const e = new Error("Vídeo eliminado"); e.fatal = true; e.cancelled = true; return e; };
  if (!st.id) {
    const { data } = await api.post("/dx-video/init", { filename: fixName(file), size: file.size, consent: true });
    Object.assign(st, { id: data.id, parts: data.parts, chunk: data.chunk_size, next: 0 });
    if (st.cancelled) { api.delete(`/dx-video/${st.id}`).catch(() => {}); throw stop(); }
  }
  while (st.next < st.parts) {
    if (st.cancelled) throw stop();
    const n = st.next;
    const blob = file.slice(n * st.chunk, (n + 1) * st.chunk);
    await withRetry(() => api.put(`/dx-video/${st.id}/part/${n}`, blob, {
      headers: { "Content-Type": "application/octet-stream" }, timeout: 120000,
      onUploadProgress: (e) => onProgress(Math.round(((n + (e.loaded / (e.total || blob.size))) / st.parts) * 100)),
    }));
    st.next = n + 1;
  }
  onProgress(100);
  await withRetry(() => api.post(`/dx-video/${st.id}/complete`));
  for (let i = 0; i < 150; i++) {
    const { data } = await withRetry(() => api.get(`/dx-video/${st.id}`));
    if (data.status === "ready") return data;
    if (data.status === "failed") { const err = new Error(data.error || "No hemos podido procesar el vídeo."); err.fatal = true; throw err; }
    await sleep(2000);
  }
  throw new Error("El procesamiento está tardando demasiado.");
};
